import { Quat, Vec3 } from 'playcanvas';

import { ElementType } from './element';
import { Events } from './events';
import { HelperPoint } from './helper-point';
import { Scene } from './scene';
import { VehicleMotionController, type VehicleMotionTargetSet, type VehicleMotionTrackSet } from './vehicle-motion-controller';

type DocVec3 = [number, number, number];
type DocQuat = [number, number, number, number];

type VehicleMotionDocBundleV0 = {
    version: 0;
    vehicleId: number | null;

    root: {
        name: string;
        visible: boolean;
        position: DocVec3;
        rotation: DocQuat;
        scale: DocVec3;
    };

    body: {
        name: string;
        visible: boolean;
    };

    wheels: Array<{
        name: string;
        visible: boolean;
        groupId: string | null;
    }>;

    tracks: VehicleMotionTrackSet;
};

const pack3 = (v: Vec3): DocVec3 => [v.x, v.y, v.z];
const pack4 = (q: Quat): DocQuat => [q.x, q.y, q.z, q.w];

const cloneTrackSet = (t: VehicleMotionTrackSet): VehicleMotionTrackSet => {
    const c = (track: VehicleMotionTrackSet['body']) => ({
        frames: [...track.frames],
        positions: [...track.positions],
        rotations: [...track.rotations]
    });

    return {
        body: c(t.body),
        wheels: [c(t.wheels[0]), c(t.wheels[1]), c(t.wheels[2]), c(t.wheels[3])]
    };
};

const safeString = (v: unknown, fallback: string) => {
    const s = typeof v === 'string' ? v : String(v ?? '');
    return s.trim() ? s : fallback;
};

const safeBool = (v: unknown, fallback = true) => {
    return (typeof v === 'boolean') ? v : fallback;
};

const safeGroupId = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    const s = v.trim();
    return s ? s : null;
};

const isDocBundleV0 = (v: any): v is VehicleMotionDocBundleV0 => {
    return (
        v && typeof v === 'object' &&
        v.version === 0 &&
        v.root && typeof v.root === 'object' &&
        v.tracks && typeof v.tracks === 'object' &&
        Array.isArray(v.wheels)
    );
};

export const registerVehicleMotionDocEvents = (scene: Scene, events: Events) => {
    events.function('docSerialize.vehicleMotion', (): VehicleMotionDocBundleV0[] => {
        const helpers = scene.getElementsByType(ElementType.helper) as HelperPoint[];
        const bundles: VehicleMotionDocBundleV0[] = [];

        for (const helper of helpers) {
            if (!helper || helper.type !== ElementType.helper) continue;

            // Only serialize imports that have an attached motion controller.
            const controller = (helper as any).vehicleMotionController as VehicleMotionController | undefined;
            const animatedHelpers = (helper as any).vehicleMotionAnimatedHelpers as { body?: HelperPoint; wheels?: HelperPoint[] } | undefined;
            if (!controller || !animatedHelpers?.body || !Array.isArray(animatedHelpers.wheels) || animatedHelpers.wheels.length !== 4) continue;

            const tracks = controller.getTracks();
            const rootPos = helper.entity.getLocalPosition();
            const rootRot = helper.entity.getLocalRotation();
            const rootScale = helper.entity.getLocalScale();

            bundles.push({
                version: 0,
                vehicleId: (helper as any).vehicleId ?? null,
                root: {
                    name: safeString(helper.name, 'v_root'),
                    visible: !!helper.visible,
                    position: pack3(rootPos),
                    rotation: pack4(rootRot),
                    scale: pack3(rootScale)
                },
                body: {
                    name: safeString(animatedHelpers.body.name, 'v_body'),
                    visible: !!animatedHelpers.body.visible
                },
                wheels: animatedHelpers.wheels.map((w, i) => ({
                    name: safeString(w?.name, `v_wheel${i + 1}`),
                    visible: !!w?.visible,
                    groupId: safeGroupId((w as any)?.groupId)
                })),
                tracks: cloneTrackSet(tracks)
            });
        }

        return bundles;
    });

    events.function('docDeserialize.vehicleMotion', async (data: any) => {
        if (!Array.isArray(data) || data.length < 1) return;

        for (const raw of data) {
            if (!isDocBundleV0(raw)) continue;

            const vehicleId = (typeof raw.vehicleId === 'number' && Number.isFinite(raw.vehicleId)) ? raw.vehicleId : null;
            const rootName = safeString(raw.root?.name, 'v_root');
            const base = rootName.replace(/_root$/, '');

            // Important: establish TRS and logical hierarchy before adding elements to the scene.
            // UI lists rebuild on elementAdded; if parentElement/childElements are attached later,
            // helpers can temporarily (or permanently) appear at the wrong hierarchy level.
            const root = new HelperPoint({
                name: rootName,
                animated: false,
                visible: safeBool(raw.root?.visible, true),
                vehicleId
            });

            if (Array.isArray(raw.root?.position) && raw.root.position.length === 3) {
                root.entity.setLocalPosition(new Vec3(raw.root.position));
            }
            if (Array.isArray(raw.root?.rotation) && raw.root.rotation.length === 4) {
                root.entity.setLocalRotation(new Quat(raw.root.rotation));
            }
            if (Array.isArray(raw.root?.scale) && raw.root.scale.length === 3) {
                root.entity.setLocalScale(new Vec3(raw.root.scale));
            }

            await scene.add(root);

            const body = new HelperPoint({
                name: safeString(raw.body?.name, `${base}_body`),
                animated: true,
                visible: safeBool(raw.body?.visible, true),
                parentEntity: root.entity,
                vehicleId
            });

            body.parentElement = root;
            root.childElements.push(body);
            await scene.add(body);

            const wheels: HelperPoint[] = [];
            for (let i = 0; i < 4; i++) {
                const wDoc: any = raw.wheels[i] ?? {};
                const w = new HelperPoint({
                    name: safeString(wDoc.name, `${base}_wheel${i + 1}`),
                    animated: true,
                    visible: safeBool(wDoc.visible, true),
                    parentEntity: root.entity,
                    vehicleId,
                    groupId: safeGroupId(wDoc.groupId)
                });
                w.parentElement = root;
                root.childElements.push(w);
                await scene.add(w);
                wheels.push(w);
            }

            const targets: VehicleMotionTargetSet = {
                body: body.entity,
                wheels: [wheels[0].entity, wheels[1].entity, wheels[2].entity, wheels[3].entity]
            };

            const controller = new VehicleMotionController(events, targets, raw.tracks);
            (root as any).vehicleMotionController = controller;
            (root as any).vehicleMotionAnimatedHelpers = { body, wheels };

            scene.forceRender = true;

            // Nudge hierarchy-dependent UIs to rebuild (best-effort).
            events.fire('hierarchy.changed');
        }
    });
};
